export type PinTuple3 = [number, number, number];

/** Yugo camera extrinsics: scalar-first quaternion (w,x,y,z), world-to-camera. */
export interface PinObserver {
  rotation: [number, number, number, number];
  translation: PinTuple3;
}

export interface ScenePin {
  name: string;
  description?: string;
  position: PinTuple3;
  observer?: PinObserver;
  tags: string[];
}

export interface PinTag { name: string; observer?: PinObserver }
export interface PinTagGroup { name: string; description?: string; tags: PinTag[] }
export interface ScenePinsAsset {
  name: string;
  description?: string;
  pins: ScenePin[];
  tag_groups: PinTagGroup[];
}

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const tuple = (v: unknown, n: number): v is number[] => Array.isArray(v) && v.length === n && v.every(x => typeof x === 'number' && Number.isFinite(x));

function observer(v: unknown): PinObserver | undefined {
  if (v == null) return undefined;
  if (!record(v) || !tuple(v.rotation, 4) || !tuple(v.translation, 3)
      || Math.hypot(...v.rotation) < 1e-8) throw new Error('Invalid pin observer');
  return { rotation: v.rotation as PinObserver['rotation'], translation: v.translation as PinTuple3 };
}

/** Validate the complete asset before creating any GPU resources. */
export function parseScenePins(value: unknown): ScenePinsAsset {
  if (!record(value) || typeof value.name !== 'string' || !value.name.trim()
      || !Array.isArray(value.pins) || value.pins.length > 100_000
      || (value.tag_groups != null && !Array.isArray(value.tag_groups))) throw new Error('Invalid pins asset');
  const pins = value.pins.map((p: unknown): ScenePin => {
    if (!record(p) || typeof p.name !== 'string' || !tuple(p.position, 3)
        || (p.tags != null && (!Array.isArray(p.tags) || !p.tags.every(t => typeof t === 'string')))) throw new Error('Invalid pin');
    return { name: p.name, description: typeof p.description === 'string' ? p.description : undefined,
      position: p.position as PinTuple3, observer: observer(p.observer), tags: (p.tags ?? []) as string[] };
  });
  const groups = ((value.tag_groups ?? []) as unknown[]).map((g): PinTagGroup => {
    if (!record(g) || typeof g.name !== 'string' || !Array.isArray(g.tags)) throw new Error('Invalid pin tag group');
    return { name: g.name, description: typeof g.description === 'string' ? g.description : undefined,
      tags: g.tags.map((tag: unknown): PinTag => {
        if (typeof tag === 'string') return { name: tag };
        if (!record(tag) || typeof tag.name !== 'string') throw new Error('Invalid pin tag');
        return { name: tag.name, observer: observer(tag.observer) };
      }) };
  });
  return { name: value.name, description: typeof value.description === 'string' ? value.description : undefined,
    pins, tag_groups: groups };
}

export function isPinsPath(path: string): boolean {
  return /\.pins(?:_v\d+)?\.json$/i.test(path.split(/[?#]/)[0]);
}
